import React from "react";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Calculator, MessageCircle } from "lucide-react";
import { formatEUR, buildWhatsappUrl, openWhatsapp, WA_MESSAGES } from "../lib/contact";
import api from "../lib/api";
import { toast } from "sonner";

const TERMS = [12, 24, 36, 48, 60, 72, 84];

// Restwert (Schlussrate) als fixer Anteil des Fahrzeugpreises — interne Konstante,
// kein UI-Toggle. Bei Ballonfinanzierung üblich: ~30 % als unverbindliche Schlussrate.
const RESIDUAL_RATIO = 0.30;

// Annuität mit Schlussrate (Ballonkredit):
//   PV (zu finanzieren) = Preis − Anzahlung
//   PV = monatlich · (1 − (1+r)^−n) / r  +  Schlussrate · (1+r)^−n
//   ⇒ monatlich = (PV − Schlussrate · (1+r)^−n) · r / (1 − (1+r)^−n)
export const calcMonthlyRate = (price, down, months, rateAnnual, residual = 0) => {
  const principal = Math.max(0, (Number(price) || 0) - (Number(down) || 0));
  const r = (Number(rateAnnual) || 0) / 100 / 12;
  const n = Number(months);
  const res = Math.max(0, Math.min(Number(residual) || 0, principal));
  if (principal <= 0 || n <= 0) return 0;
  if (r === 0) return (principal - res) / n;
  const discountFactor = Math.pow(1 + r, -n);
  return ((principal - res * discountFactor) * r) / (1 - discountFactor);
};

const FinancingCalculator = ({ initialVehicle = null }) => {
  const [price, setPrice] = React.useState(initialVehicle?.price || 30000);
  const [downPayment, setDownPayment] = React.useState(3000);
  const [term, setTerm] = React.useState(48);
  const [rate, setRate] = React.useState(6.9);

  const [contact, setContact] = React.useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);

  const residual = Math.round((Number(price) || 0) * RESIDUAL_RATIO);
  const monthly = calcMonthlyRate(price, downPayment, term, rate, residual);
  const total = monthly * term + Number(downPayment || 0) + residual;

  const vehicleLabel = initialVehicle
    ? `${initialVehicle.make} ${initialVehicle.model}`
    : `Betrag ${formatEUR(price)}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.phone) {
      toast.error("Bitte Name und Telefonnummer angeben.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/financing-requests", {
        vehicle_id: initialVehicle?.id || null,
        vehicle_name: initialVehicle ? vehicleLabel : "",
        vehicle_price: Number(price),
        down_payment: Number(downPayment),
        term_months: Number(term),
        interest_rate: Number(rate),
        monthly_rate: Number(monthly.toFixed(2)),
        residual_value: Number(residual),
        ...contact,
      });
      toast.success("Finanzierungsanfrage gesendet. Wir melden uns rasch bei Ihnen.");
      setContact({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Senden fehlgeschlagen. Bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--na-red)]/15 text-[var(--na-red)] flex items-center justify-center">
            <Calculator className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-xl font-semibold text-white">Finanzierungsrechner</h3>
            <p className="text-xs text-zinc-500">Beispielrechnung in EUR · Partnerbank</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <div className="flex items-baseline justify-between">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-400">Fahrzeugpreis</Label>
              <span className="text-white text-sm font-mono">{formatEUR(price)}</span>
            </div>
            <Slider data-testid="financing-price-slider" value={[price]} onValueChange={(v) => setPrice(v[0])} min={3000} max={150000} step={500} className="mt-3" />
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-400">Anzahlung</Label>
              <span className="text-white text-sm font-mono">{formatEUR(downPayment)}</span>
            </div>
            <Slider data-testid="financing-down-slider" value={[downPayment]} onValueChange={(v) => setDownPayment(v[0])} min={0} max={Math.max(0, price - 1000)} step={500} className="mt-3" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">Laufzeit</Label>
              <Select value={String(term)} onValueChange={(v) => setTerm(Number(v))}>
                <SelectTrigger data-testid="financing-term-select" className="bg-[#1A1A22] border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#232330] border-white/10 text-white">
                  {TERMS.map((t) => (<SelectItem key={t} value={String(t)} className="focus:bg-white/10 focus:text-white">{t} Monate</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">Zinssatz (eff. % p.a.)</Label>
              <Input data-testid="financing-rate-input" type="number" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="bg-[#1A1A22] border-white/10 text-white" />
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-white/5 grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="text-center flex flex-col">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 leading-tight min-h-[2.4em] flex items-end justify-center">Monatliche<br/>Rate</p>
            <p data-testid="financing-monthly-rate" className="font-display text-lg lg:text-base xl:text-lg font-bold text-[var(--na-red)] mt-2 tracking-tight whitespace-nowrap">
              {formatEUR(monthly)}
            </p>
          </div>
          <div className="text-center flex flex-col">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 leading-tight min-h-[2.4em] flex items-end justify-center">Anzahlung</p>
            <p data-testid="financing-down-result" className="font-display text-lg lg:text-base xl:text-lg font-bold text-white mt-2 whitespace-nowrap">{formatEUR(downPayment)}</p>
          </div>
          <div className="text-center flex flex-col">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 leading-tight min-h-[2.4em] flex items-end justify-center">Laufzeit</p>
            <p className="font-display text-lg lg:text-base xl:text-lg font-bold text-white mt-2 whitespace-nowrap">{term} M.</p>
          </div>
          <div className="text-center flex flex-col">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 leading-tight min-h-[2.4em] flex items-end justify-center">Schlussrate</p>
            <p data-testid="financing-residual" className="font-display text-lg lg:text-base xl:text-lg font-bold text-white mt-2 whitespace-nowrap">{formatEUR(residual)}</p>
          </div>
          <div className="text-center flex flex-col col-span-2 lg:col-span-1">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 leading-tight min-h-[2.4em] flex items-end justify-center">Gesamt</p>
            <p data-testid="financing-total" className="font-display text-lg lg:text-base xl:text-lg font-bold text-white mt-2 whitespace-nowrap">{formatEUR(total)}</p>
          </div>
        </div>

        <p className="text-xs text-zinc-500 leading-relaxed">
          Unverbindliche Beispielrechnung mit Ballonfinanzierung: Die Schlussrate (ca. {Math.round(RESIDUAL_RATIO * 100)} % des Fahrzeugpreises) wird am Laufzeitende fällig. Finanzierung vorbehaltlich Bonitätsprüfung und Partnerbank.
        </p>
      </div>

      <form onSubmit={submit} className="bg-[#2E2E3C] border border-silver-soft rounded-2xl p-6 lg:p-8 space-y-5">
        <h3 className="font-display text-xl font-semibold text-white">Finanzierungsanfrage senden</h3>
        <p className="text-sm text-zinc-400">
          Sie erhalten innerhalb eines Werktags ein konkretes, unverbindliches Finanzierungsangebot.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormInput testId="finanz-name" label="Name *" value={contact.name} onChange={(v) => setContact((c) => ({ ...c, name: v }))} />
          <FormInput testId="finanz-phone" label="Telefon *" value={contact.phone} onChange={(v) => setContact((c) => ({ ...c, phone: v }))} />
          <FormInput testId="finanz-email" label="E-Mail" type="email" value={contact.email} onChange={(v) => setContact((c) => ({ ...c, email: v }))} />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">Nachricht</Label>
          <textarea
            data-testid="finanz-message"
            value={contact.message}
            onChange={(e) => setContact((c) => ({ ...c, message: e.target.value }))}
            rows={4}
            className="w-full rounded-xl bg-[#1A1A22] border border-white/10 text-white px-3 py-2 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[var(--na-red)]"
            placeholder="Zusätzliche Informationen, z.B. Wunschrate, gewünschtes Fahrzeug…"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <Button type="submit" disabled={submitting} data-testid="finanz-submit-button" className="bg-[var(--na-red)] hover:bg-[var(--na-red-hover)] text-white rounded-xl font-medium tracking-wide">
            {submitting ? "Wird gesendet…" : "Anfrage senden"}
          </Button>
          <a
            href={buildWhatsappUrl(WA_MESSAGES.financing(vehicleLabel))}
            onClick={openWhatsapp(WA_MESSAGES.financing(vehicleLabel))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="finanz-whatsapp-link"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#25D366] text-[#1A1A22] text-sm font-medium hover:bg-[#20bd5a] transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        </div>
      </form>
    </div>
  );
};

const FormInput = ({ label, value, onChange, testId, type = "text" }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 block">{label}</Label>
    <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="bg-[#1A1A22] border-white/10 text-white focus-visible:ring-[var(--na-red)] focus-visible:border-[var(--na-red)]" />
  </div>
);

export default FinancingCalculator;
